// ห้ามแทรก — the breaks a doctor keeps inside their own shift.
//
// A block is its OWN `appointment` row, not a mode of the shift row: same
// doctor + branch + date, `block: true`, no patient, and an EMPTY `event_type`
// (confirmed against live rows — only `block === true` marks a break; the shift
// row's `title: "block"` is Bubble's auto-label and means nothing). The booking
// day view paints these as the striped bands nobody may book into, and the
// clinic creates them there too — this module is the second writer, so it
// matches the existing rows field for field rather than inventing a shape.
//
//   title       'ห้ามแทรก'   what Bubble writes; the day view reads `details`
//   event_color '#fce9e9'    the pink every existing block already carries
//   start_date / end_date    epoch ms  ·  duration  minutes
//
// Times are Asia/Bangkok and so is the browser, so local getters are the
// conversion — no offset arithmetic here.
import { parseDate } from './asset.js'

export const BLOCK_TITLE = 'ห้ามแทรก'
export const BLOCK_COLOR = '#fce9e9'

// The grid a break is dragged on. 15 minutes is the clinic's own booking
// granularity, so a block can always line up with the slot it is protecting.
export const SNAP_MIN = 15
// Below this a band is too thin to grab, and a 5-minute break is not a thing
// anyone rosters.
export const MIN_BLOCK_MIN = 15

const pad = (n) => String(n).padStart(2, '0')

export const isBlockRow = (r) => (
  r?.block === true && !Number(r?.patient_id) && r?.active !== false
)

/* ── minutes-of-day, the unit the timeline thinks in ─────────────────────── */
export function minOfDay(ts) {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return null
  const d = new Date(n)
  return d.getHours() * 60 + d.getMinutes()
}

export const fmtMin = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`

export function minOfTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim())
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

export function tsOfMin(dateKey, min) {
  const d = parseDate(dateKey)
  if (!d) return null
  d.setHours(0, Math.round(min), 0, 0)
  return d.getTime()
}

export const snap = (min, step = SNAP_MIN) => Math.round(min / step) * step
export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

/* ── the blocks of one shift ─────────────────────────────────────────────── */
// The day's rows narrowed to breaks that belong to THIS shift: same doctor and
// branch, and overlapping the shift window. A doctor working two branches in one
// day has two shift rows, and a break belongs to the one it sits inside.
export function blocksOfShift(rows, { doctorId, branchId, start, end }) {
  return (Array.isArray(rows) ? rows : [])
    .filter(isBlockRow)
    .filter((r) => Number(r.doctor_id) === Number(doctorId) && Number(r.branch_id) === Number(branchId))
    .map((r) => ({ row: r, s: minOfDay(r.start_date), e: minOfDay(r.end_date) }))
    .filter((b) => b.s != null && b.e != null && b.e > b.s)
    .filter((b) => start == null || end == null || (b.s < end && b.e > start))
    .sort((a, b) => a.s - b.s)
}

export const overlaps = (a, b) => a.s < b.e && b.s < a.e

// Where a NEW break should land when the operator presses "+" instead of
// dragging: the first gap inside the shift big enough to hold it. Returns null
// when there is none — a suggestion that overlapped an existing break would be
// a worse answer than "there is no room", since dragging cannot produce one.
export function suggestBlock(existing, start, end, len = 60) {
  const want = Math.min(len, Math.max(MIN_BLOCK_MIN, end - start))
  let cursor = snap(start)
  for (const b of [...existing].sort((x, y) => x.s - y.s)) {
    if (b.s - cursor >= want) return { s: cursor, e: cursor + want }
    cursor = Math.max(cursor, snap(b.e))
  }
  return end - cursor >= want ? { s: cursor, e: cursor + want } : null
}

/* ── the wire payload ────────────────────────────────────────────────────── */
export function blockPayload({ dateKey, doctorId, branchId, s, e, details }) {
  const start = tsOfMin(dateKey, s)
  const end = tsOfMin(dateKey, e)
  return {
    doctor_id: Number(doctorId) || 0,
    branch_id: Number(branchId) || 0,
    date: dateKey,
    start_date: start,
    end_date: end,
    duration: Math.round((end - start) / 60000),
    details: String(details || '').trim(),
  }
}
